const normalizeEmail = (value) => String(value || "").trim().toLowerCase();

const isSameApprovalStep = (step, level, approver) =>
  Number(step?.level) === Number(level) &&
  normalizeEmail(step?.approver) === normalizeEmail(approver);

/**
 * Apply a successful approval action to the quotation already on screen.
 * The API remains the source of truth; this only avoids a full-page reload
 * after the server has accepted the action.
 */
export const applyApprovalActionToQuotation = (
  quotation,
  { level, approver, status, actedAt = new Date().toISOString() },
) => {
  if (!quotation) return quotation;

  const approvalHierarchy = (quotation.approvalHierarchy || []).map(
    (approval, approvalIndex) => {
      if (approvalIndex !== 0) return approval;

      const steps = approval?.approvalHierarchy || [];
      const exactMatchExists = steps.some((step) =>
        isSameApprovalStep(step, level, approver),
      );

      const nextSteps = steps.map((step) => {
        const matches = exactMatchExists
          ? isSameApprovalStep(step, level, approver)
          : Number(step?.level) === Number(level);

        if (!matches) return step;

        return {
          ...step,
          status,
          ...(status === "Approved" ? { approvedAt: actedAt } : {}),
        };
      });

      return {
        ...approval,
        approvalHierarchy: nextSteps,
      };
    },
  );

  const steps = approvalHierarchy[0]?.approvalHierarchy || [];
  const approvalStatus =
    status === "Rejected" || status === "Canceled"
      ? status
      : steps.length > 0 && steps.every((step) => step.status === "Approved")
        ? "Approved"
        : quotation.approvalStatus;

  return {
    ...quotation,
    approvalStatus,
    approvalHierarchy,
  };
};

export const resetQuotationApprovalState = (quotation) => {
  if (!quotation) return quotation;

  return {
    ...quotation,
    approvalStatus: "Pending",
    reason: "",
    approvalHierarchy: (quotation.approvalHierarchy || []).map((approval) => ({
      ...approval,
      approvalHierarchy: (approval?.approvalHierarchy || []).map((step) => ({
        ...step,
        status: "Pending",
        approvedAt: null,
      })),
    })),
  };
};
